// Per-driver operational detail. For every driver we manufacture
// believable state for the driver detail card:
//   - current activity (in-run target gate, at-gate role, on lunch with
//     return time, etc.)
//   - today's run history (completed runs with bag counts + confidence)
//   - tug assignment (if any)
//   - sector + zone they cover
//   - performance KPIs (runs done today, hot bags made/missed)
//
// Static seed for now — easy to wire to real data later.

import { DRIVERS, GATE_BY_ID } from './mockLiveState';

function makeRng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rng = makeRng(11421);
function rand(lo, hi) { return Math.floor(rng() * (hi - lo + 1)) + lo; }
function pick(arr) { return arr[Math.floor(rng() * arr.length)]; }

const SECTORS = ['Sector 1', 'Sector 2', 'Sector 3', 'Sector 4'];

// Build a fake "today" run history (mins-since-shift-start)
function buildHistory(rng) {
  const count = rand(2, 6);
  const out = [];
  let mins = 5 * 60 + rand(0, 30);  // shift starts ~05:00
  for (let i = 0; i < count; i++) {
    const dur = rand(28, 75);
    const flightNum = `WN ${rand(100, 4900)}`;
    const fromGate = `C${rand(23, 67)}`;
    const bags = rand(2, 24);
    const hotMade = rng() > 0.18;
    out.push({
      id: `h-${i}`,
      flightNumber: flightNum,
      gate: fromGate,
      startMin: mins,
      durationMin: dur,
      bags,
      hadHot: rng() > 0.55,
      hotMade,
    });
    mins += dur + rand(8, 25);
  }
  return out;
}

function fmtClock(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export const DRIVER_DETAILS = (() => {
  const map = new Map();
  DRIVERS.forEach(d => {
    const history = buildHistory(rng);
    const totalBags  = history.reduce((s, h) => s + h.bags, 0);
    const hotsMade   = history.filter(h => h.hadHot && h.hotMade).length;
    const hotsMissed = history.filter(h => h.hadHot && !h.hotMade).length;
    const sector     = SECTORS[Math.floor(rng() * SECTORS.length)];

    // Current activity — derived from driver.status
    let current = null;
    if (d.status === 'in_run' && d.targetGateId) {
      const target = GATE_BY_ID.get(d.targetGateId);
      current = {
        kind: 'in_run',
        toGate: d.targetGateId,
        fromGate: history[history.length - 1]?.gate || `C${rand(23, 67)}`,
        flightNumber: `WN ${rand(100, 4900)}`,
        etaMin: rand(2, 9),
        bags: rand(3, 18),
      };
    } else if (d.status === 'at_gate' && d.gateId) {
      current = {
        kind: 'at_gate',
        gate: d.gateId,
        flightNumber: `WN ${rand(100, 4900)}`,
        role: pick(['FWD', 'AFT']),
      };
    } else if (d.status === 'on_lunch') {
      const startedMin = 7 * 60 + rand(0, 25);
      current = {
        kind: 'on_lunch',
        startedAt: fmtClock(startedMin),
        returnsAt: fmtClock(startedMin + 30),
        durationMin: 30,
      };
    } else if (d.status === 'on_break') {
      const startedMin = 7 * 60 + rand(15, 35);
      current = {
        kind: 'on_break',
        startedAt: fmtClock(startedMin),
        returnsAt: fmtClock(startedMin + 15),
        durationMin: 15,
      };
    } else {
      current = { kind: 'idle' };
    }

    // Issues
    const issues = [];
    // 8% chance: hasn't accepted current assignment
    if (rng() < 0.08 && current.kind === 'in_run') {
      issues.push({ kind: 'unaccepted', severity: 'gold',
        label: `Hasn't accepted assignment (${current.toGate})` });
    }
    // 5% chance: late
    if (rng() < 0.05) {
      issues.push({ kind: 'late', severity: 'red',
        label: `Late to ${current.toGate || 'gate'} by ${rand(3, 12)} min` });
    }
    // 3% chance: tug down
    if (rng() < 0.03) {
      issues.push({ kind: 'tug_down', severity: 'gold',
        label: 'Tug reported brake issue' });
    }

    map.set(d.id, {
      driverId: d.id,
      name: d.name,
      badge: d.badge,
      seniority: d.seniority,
      shiftCode: d.shiftCode || '6-14a',
      sector,
      status: d.status,
      tug: rng() > 0.08 ? `T-${rand(1000, 9999)}` : null,
      current,
      history,
      runsToday: history.length,
      bagsHandled: totalBags,
      hotsMade,
      hotsMissed,
      issues,
    });
  });
  return map;
})();

export const DRIVER_STATUS_DISPLAY = {
  on_shift: { label: 'On Shift',  color: '#94A3B8' },
  in_run:   { label: 'In Run',    color: '#00BCD4' },
  at_gate:  { label: 'At Gate',   color: '#F9B612' },
  on_break: { label: 'On Break',  color: '#9C27B0' },
  on_lunch: { label: 'On Lunch',  color: '#EC4899' },
};
